<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useCustomersStore } from '@/store/customers'
import Progress from '@/components/forms/Progress.vue'
import { getFirstFieldError, normalizeApiError } from '@/utils/apiError'

const route = useRoute()
const router = useRouter()
const store = useCustomersStore()

const id = computed(() => route.params.id)
const isNew = computed(() => id.value === 'new')

const name = ref('')
const email = ref('')
const phone = ref('')
const type = ref('')
const errorMessage = ref('')
const fieldErrors = ref({})

function clearFeedback() {
  errorMessage.value = ''
  fieldErrors.value = {}
}

function getFieldError(fieldName) {
  return getFirstFieldError(fieldErrors.value, fieldName)
}

function applyApiError(error, fallbackMessage) {
  const apiError = normalizeApiError(error, { fallbackMessage })
  errorMessage.value = apiError.message
  fieldErrors.value = apiError.fieldErrors
}

function resetForm() {
  name.value = ''
  email.value = ''
  phone.value = ''
}

function fillForm(customerData = {}) {
  name.value = customerData.name || ''
  email.value = customerData.email || ''
  phone.value = customerData.phone || ''
}

async function loadCustomer() {
  clearFeedback()

  if (isNew.value) {
    resetForm()
    type.value = ''
    return
  }

  type.value = 'loading'
  try {
    await store.getCustomer(id.value)
    fillForm(store.currentCustomer || {})
    type.value = ''
  } catch (error) {
    type.value = 'error'
    applyApiError(error, 'Не вдалося завантажити клієнта.')
  }
}

async function updateCustomer() {
  clearFeedback()

  if (isNew.value) {
    resetForm()
    type.value = 'success'
    return
  }

  type.value = 'loading'
  try {
    await store.getCustomer(id.value)
    fillForm(store.currentCustomer || {})
    type.value = 'success'
  } catch (error) {
    type.value = 'error'
    applyApiError(error, 'Не вдалося оновити дані форми.')
  }
}

async function saveCustomer() {
  type.value = 'loading'
  clearFeedback()
  const sendData = {}

  if (name.value) sendData.name = name.value
  if (email.value) sendData.email = email.value
  if (phone.value) sendData.phone = phone.value

  try {
    if (isNew.value) {
      const created = await store.addCustomer(sendData)
      const createdId = created._id?.$oid || created._id || created.id
      type.value = 'success'
      if (createdId) {
        await router.replace(`/customers/${createdId}`)
      }
      return
    }

    await store.updateCustomer(id.value, sendData)
    type.value = 'success'
  } catch (error) {
    type.value = 'error'
    applyApiError(error, 'Не вдалося зберегти клієнта.')
  }
}

onMounted(loadCustomer)
watch(() => route.params.id, loadCustomer)
</script>

<template>
  <div class="container mt-4">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h2>{{ route.meta.title }}</h2>
      <Progress :type="type" :error-message="errorMessage" />
      <div>
        <button class="btn btn-outline-secondary me-2" @click="updateCustomer" :disabled="type === 'loading'">
          <i class="bi bi-arrow-clockwise"></i>
        </button>
        <button class="btn btn-primary" @click="saveCustomer" :disabled="type === 'loading'">
          <i class="bi bi-save"></i>
        </button>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="card-body">
        <div class="row mb-3">
          <div class="col-md-4 d-flex justify-content-end align-items-center">Ім'я</div>
          <div class="col-md-8">
            <input v-model="name" type="text" class="form-control" :class="{ 'is-invalid': !!getFieldError('name') }" />
            <div v-if="getFieldError('name')" class="invalid-feedback d-block">
              {{ getFieldError('name') }}
            </div>
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-md-4 d-flex justify-content-end align-items-center">Email</div>
          <div class="col-md-8">
            <input v-model="email" type="email" class="form-control" :class="{ 'is-invalid': !!getFieldError('email') }" />
            <div v-if="getFieldError('email')" class="invalid-feedback d-block">
              {{ getFieldError('email') }}
            </div>
          </div>
        </div>
        <div class="row mb-3">
          <div class="col-md-4 d-flex justify-content-end align-items-center">Телефон</div>
          <div class="col-md-8">
            <input v-model="phone" type="text" class="form-control" :class="{ 'is-invalid': !!getFieldError('phone') }" />
            <div v-if="getFieldError('phone')" class="invalid-feedback d-block">
              {{ getFieldError('phone') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">

</style>
